// 8.3.1 - Get List of Packages and Standalone Products
// by Client’s Suboccupation ID and Client Entity Code

// Interface for ProductSection
export interface ProductSection {
	id: number;
	name: string;
	liability: number[];
	excess: number[];
}

// Interface for Product
export interface Product {
	id: number;
	name: string;
	isMainForPackage: boolean;
	productSections: ProductSection[];
}

// Interface for Package
export interface Package {
	id: number;
	name: string;
	products: Product[];
}

// Interface for the response from the endpoint
export interface GetProductsResponse {
	id: number;
	products: Product[];
	packages: Package[];
}

///////
// 8.5 - Get Client Suboccupation by ABN Number

// Interface for Suboccupation
export interface Suboccupation {
	id: number;
	name: string;
	occupation: string | null;
}

// Interface for Occupation
export interface Occupation {
	id: number;
	name: string;
	subOccupations: Suboccupation[];
}

// Interface for the response from the endpoint
export type GetClientSuboccupationResponse = Occupation[];

///////
// 8.1 - Get ABN Clients by Name

// Interface for ABN Client
export interface AbnClient {
	businessNumber: string;
	isCurrentName: string;
	organizationName: string;
	isCurrentAddress: string;
	postcode: string;
	stateCode: string;
	searchRelevance: string;
	fullName: string | null;
	givenName: string | null;
	otherGivenName: string | null;
	familyName: string | null;
	tradingName: string | null;
	businessName: string | null;
}

// Interface for the response from the endpoint
export type GetAbnClientsByNameResponse = AbnClient[];

export interface AcnClientIdentifier {
	numberHeading: string;
	number: string;
}

export interface AcnClientName {
	nameValue: string;
}

export interface AcnClientType {
	code: string;
	description: string;
	isRegistered?: boolean;
}

export interface AcnClientAddress {
	type: string;
	addressLine: string;
	iso3166CountryCode: string;
	locality: string;
	postCode: string;
	state: string;
}

export interface AcnClient {
    identifier: AcnClientIdentifier;
    name: AcnClientName;
    type: AcnClientType;
    class: AcnClientType;
    subClass: AcnClientType;
    status: AcnClientType;
    dateRegistered: string;
    dateReview: any;
    address: AcnClientAddress[];
    abrEntity: AbrEntity | null;
}

export interface AbrEntity {
    abn: string;
    entityName: string;
    entityType: string;
}

// {
//     "result": {
//         "identifier": {
//             "numberHeading": "ARBN",
//             "number": "550225806"
//         },
//         "name": {
//             "nameValue": "401 CERT TEST 1"
//         },
//         "type": {
//             "code": "RACN",
//             "description": "Registered Australian Body"
//         },
//         "class": {
//             "code": "NONE",
//             "description": "Does Not Have An Equivalent Australian Liability"
//         },
//         "subClass": {
//             "code": "RACO",
//             "description": "Registrable Australian Corp - Non Association"
//         },
//         "status": {
//             "code": "REGD",
//             "description": "Registered",
//             "isRegistered": true
//         },
//         "dateRegistered": "2001-07-10",
//         "dateReview": null,
//         "address": [
//             {
//                 "type": "RP",
//                 "addressLine": "RESTRICTED",
//                 "iso3166CountryCode": "AU",
//                 "locality": "SYDNEY",
//                 "postCode": "2000",
//                 "state": "NSW"
//             }
//         ],
//         "abrEntity": null
//     }
// }
///////
// 8.2 - Get ABN Client by ABN Number
// Interface for ABN Client details
export interface AbnClientDetails {
	entityStatusCode: string;
	status: string | null;
	effectiveFrom: string;
	effectiveTo: string;
	asicNumber: string;
	type: string;
	typeDescription: string;
	gstEffectiveFrom: string | null;
	addressLine1: string;
	addressLine2: string;
	suburb: string;
	countryName: string;
	suboccupationId: number | null;
	businessNumber: string;
	isCurrentName: string | null;
	organizationName: string;
	isCurrentAddress: string;
	postcode: string;
	stateCode: string;
	searchRelevance: string;
	fullName: string | null;
	givenName: string | null;
	otherGivenName: string | null;
	familyName: string | null;
	tradingName: string | null;
	businessName: string | null;
}

export enum ApiSearchTypes {
	ABN = 'ABN API Search',
	ACN = 'ACN API Search',
}

export interface ClientEntityGroup {
	id: number;
	name: string;
	clientEntityGroupDescription: string;
	searchFlag: string;
}
///////

///////
// 10 - Quote Microservice Endpoints

// 10.1.1 QuotePackage
export interface QuotePackage {
	id: number;
	name: string;
	quoteProducts: QuoteProduct[];
    referralReasons: QuoteReferralReason[];
}

// 10.1.11.2 QuoteProduct (Update/Bind Quote Group Response)
export interface QuoteProduct {
	id: number;
	quoteLineId: number;
	referralReason?: number;
	name: string;
	isMainForPackage?: boolean;
	quoteProductDecisionTree?: DecisionTree;
	quoteProductSections: QuoteProductSection[];
    referralReasons: QuoteReferralReason[];
}

// 10.1.2.1 QuoteGroup (Create Quote Group Request)
export interface QuoteGroupCreateRequest {
	id: number;
	readOnly: boolean;
	quoteClient: Client;
	quoteBroker: Broker;
	quotes: QuoteCreateRequest[];
    referralReasons?: QuoteReferralReason[];
}

export interface QuoteClientSanctions {
	clientId?: number | null;
	id: number;
	numOfResults: number;
	searchDateTime: string;
	searchId: number;
	searchName: string;
	searchReference: string;
	searchType: string;
}

// 10.1.2.2 QuoteGroup (Create/Update/Bind Quote Group Response)
export interface QuoteGroupResponse {
	id: number;
	readOnly: boolean;
	quoteClient: Client;
	quoteBroker: Broker;
	quoteClientSanctions?: QuoteClientSanctions | null;
	decisionTreeGeneralQuestions: DecisionTree;
	decisionTreeUnderwriting?: DecisionTree;
	quotes: Quote[];
    referralReasons: QuoteReferralReason[];
}

// 10.1.3 Client
export interface Client {
	id: number;
	businessNumber: string | null;
	acnBusinessNumber: string | null;
	organizationName: string;
	description: string;
	website: string;
	phone: string;
	email: string;
	clientHash: string;
	address1: string;
	address2: string;
	postcode: string;
	stateCode: string;
	countryId: number;
	countryName: string;
	suboccupationId: number;
	suboccupationName: string;
	clientEntityId: number;
	clientEntityName: string;
	clientAbnEntityCode: string;
    clientAcnEntityId?: 1 | 2;
    clientAcnEntityName?: 'Australian Proprietary Company' | 'Australian Public Company';
    clientAcnEntityCode?: 'APTY' | 'APUB'
}
// clientAcnEntityId
// Client ACN Entity Ids:
//  1 if Private Company
//  2 if Public Listed

// clientAcnEntityName
//  “Australian Proprietary Company” if Private Company
//  “Australian Public Company” if Public Listed

// clientAcnEntityCode
//  APTY if Australian Proprietary Company
//  APUB if Australian Public Company

// 10.1.4 Broker
export interface Broker {
	id: number;
	firstName: string;
	lastName: string;
	email: string;
	phone?: string | null;
	mobile?: string | null;
	isActive?: boolean;
	noLongerWorksHere?: boolean | null;

	brokerFirm?: BrokerFirm | null;

	brokerFirmId?: number;
	brokerFirmName?: string;
	brokerFirmEmail?: string;
	brokerFirmPhone?: string;
	brokerFirmAddress?: string | null;
	brokerFirmWebSite?: string | null;
	isBrokerFirmActive?: boolean | null;
	brokerFirmBrokerage?: number;
	brokerFirmTypeId?: number;
	brokerFirmTypeName?: string;
	brokerFirmNotGSTRegistered?: boolean;

	xeroLocationId?: number;
	xeroLocation?: string;
	xeroPaymentPeriodDays?: number;
	authorisedRepNetworkId?: string | number | null;
	clusterGroupId?: number;
	clusterGroupName?: string;
}

// 10.1.5 BrokerFirm
export interface BrokerFirm {
	id: number;
	name: string;
}

// 10.1.6.1 Quote (Create Quote Group Request)
export interface QuoteCreateRequest {
	id: number;
	policyId: number;
	readOnly: boolean;
	RecordState: number;
	brokeragePercentage: number;
	totalTurnoverAmountLastYear?: number | null;
	totalTurnoverAmountCurrentYear?: number | null;
	policyPeriodStartDate?: string;
	policyPeriodEndDate?: string;
	quoteRatingRegions: QuoteRatingRegion[];
	quoteProducts: QuoteProduct[];
	quotePackages: QuotePackage[];
}

// 10.1.6.2 Quote (Create/Update/Bind Quote Group Response)
export interface Quote {
	id: number;
	policyId: number;
	readOnly: boolean;
	brokeragePercentage: number;
	lastModifiedDate?: string;
	status: QuoteStatus;
	quoteUnderWriter?: QuoteUnderWriter;
	RecordState: number;
	totals: QuoteTotals;
	totalTurnoverAmountLastYear?: number;
	totalTurnoverAmountCurrentYear?: number;
	totalStaffNumbers?: number;
	policyPeriodStartDate?: string;
	policyPeriodEndDate?: string;
	policyPeriodStartDateBackdatingDays?: number;
	policyPeriodStartDateFutureDatingDays?: number;
	policyPeriodEndDateMinMonths: number;
	policyPeriodEndDateMaxMonths: number;
	quoteRatingRegions: QuoteRatingRegion[];
	quoteProducts: QuoteProduct[];
	quotePackages: QuotePackage[];
	nonApplicablePolicyDocuments?: any[];
}

// 10.1.7 Status
export interface QuoteStatus {
	id: number;
	name: string;
	statusNumber: number;
}

// 10.1.8 QuoteUnderWriter
export interface QuoteUnderWriter {
	fullName: string;
	mobile: string;
	phone: string;
	email: string;
}

// 10.1.9 QuoteTotals
export interface QuoteTotals {
	adminFee: number;
	adminFeeGst: number;
	basePremium: number;
	basePremiumGst: number;
	totalPremium: number;
	nettPayable: number;
	stampDuty: number;
	commission: number;
	commissionGst: number;
}

// 10.1.10 QuoteRatingRegion
export interface QuoteRatingRegion {
	id: number;
	lastYearAmount: number;
	currentYearAmount: number;
	turnoverPercentageLastYear?: number | null;
	turnoverPercentageCurrentYear?: number | null;
	staffNumbers: number;
	totalAssets: number;
	ratingRegionId: number;
	ratingRegionName: string;
}

// 10.1.13 QuoteRatingParameter
export interface QuoteRatingParameter {
	id: number;
	question: string;
	quoteRatingParameterOptions: QuoteRatingParameterOption[];
}

// 10.1.14 QuoteRatingParameterOption
export interface QuoteRatingParameterOption {
	id: number;
	value: string;
	description: string;
}

// 10.1.15 DecisionTree
export interface DecisionTree {
	id: number;
	type: number;
	productId: number;
	foreignEntityInstanceId: number;
	name: string;
	decisionTreeAction: DecisionTreeAction;
	decisionTreeQuestions: DecisionTreeQuestion[];
}

export interface DecisionTreeOptionMessage {
    id: number;
    message: string;
    decisionTreeOption: null;
}

// 10.1.16 DecisionTreeAction
export interface DecisionTreeAction {
	id: number;
	type: number;
	priority: number;
	name: string;
}

// 10.1.17 DecisionTreeQuestion
export interface DecisionTreeQuestion {
	id: number;
	type: number;
	name: string;
	questionText: string;
	decisionTreeOptions: DecisionTreeOption[];
	freeTextAnswer: string;
}

// 10.1.18 DecisionTreeOption
export interface DecisionTreeOption {
	id: number;
	type: number;
	name: string;
	text: string;
	selected: boolean;
    position: number;
    decisionTreeOptionMessages: DecisionTreeOptionMessage[] | null;
	decisionTreeOptionAction: DecisionTreeAction;
	decisionTreeOptionQuestion: DecisionTreeQuestion | null;
}

export interface RiskParameterAvailableValues {
    id: number;
    name: string | null;
    value: number;
}
export interface QuoteSectionRiskParameters {
    id: number;
    name: string;
    value: number;
    riskParameterAvailableValues: RiskParameterAvailableValues[];
    riskParameterOptions: RiskParameterAvailableValues[];
}
export interface QuoteSectionRiskTrade {
    id: number;
    name: string;
    quoteSectionRiskParameters: QuoteSectionRiskParameters[];
}
// 10.1.12.1 QuoteProductSection (Create Quote Group Request)
export interface QuoteProductExcess {
    id: number;
    value: number;
}
export interface QuoteProductSection {
	id: number;
	productSectionId: number;
	productSectionName: string;
	liability: number;
	excess: number;
	premium: number | null;

	// 10.1.12.2 QuoteProductSection (Update/Bind Quote Group Response)
	ratingDefinitionId?: number;
	ratingDefinitionName?: string;
	inceptionDate?: string;
	totals?: QuoteTotals;
	quoteRatingParameters?: QuoteRatingParameter[];

    quoteSectionRiskTrades?: QuoteSectionRiskTrade[];
    availableExcesses?: QuoteProductExcess[];
	[key: string]: any;
}

// Union type for DecisionTree or QuoteProduct
export type DecisionTreeOrQuoteProduct = DecisionTree | QuoteProduct;

export interface QuoteReferralReason {
    id: number;
    referralReasonId: number;
    quoteSectionId: number;
    name: string;
}

export interface ReferralReasonDisplay extends QuoteReferralReason {
    type: string;
    productName?: string;
    packageName?: string;
    productId?: number;
    packageId?: number;
}